import React from 'react';
import { Icon } from 'semantic-ui-react';
import { GiBackPain } from 'react-icons/gi';
import { LiaSmileWinkSolid } from 'react-icons/lia';
import { TbFileSmile } from 'react-icons/tb';
import stand_alert from '../../Images/Position/stand-alert.png';
import SPO2_grey from '../../Images/SPO2/spo2_grey.png';
import bloodPressure_grey from '../../Images/BloodPressure/BloodPressure_grey.png';
import weight_grey from '../../Images/Weight/Weight_grey.png';
import img_vni from '../../Images/Devices/RESPIRATION.png';
import img_leakage from '../../Images/Devices/LEAKAGE.png';
import img_oxygentherapy from '../../Images/Devices/OXYGEN_MASK.png';
import healthscale_grey from '../../Images/Healthform/health_grey.png';
import ruler_grey from '../../Images/Healthform/ruler_grey.png';

// Metric descriptors for the responsive card view (PatientCardList).
// Duplicates the per-metric metadata of GridColDefList in PatientSimpleList.js
// (feature bit, alert key, color behavior, format key, label, icon). Any change
// to a metric column there must be mirrored here until both consume one source.
//
// colorMode:
//   'full'       — text color from Utils.dataAlertColor, yellow bg when alert activated and color is red
//   'bgOnly'     — default text color, yellow bg when alerts[alertKey].triggered (tuckyPosition)
//   'fixedGreen' — '#21BA45' text ('#767676' when '-'), yellow bg rule only if alertKey set
//   'none'       — no coloring at all
// formatKey: argument for Utils.formatDataDisplay, null = raw value
// tappable: false when the grid opens no graph for this field (see handleCellClick)

const imgIcon = (src, alt) => (
    <img src={src} style={{ height: 20, width: 'auto', maxWidth: 28 }} alt={alt} />
);

const patientMetricDescriptors = [
    { field: 'tuckyTemperature', featureName: 'thermometer', alertKey: 'fever_alert', colorMode: 'full',
        formatKey: 'fever_alert', labelId: 'patientConstants.temperature', defaultLabel: 'Temperature',
        renderIcon: () => <Icon name="thermometer half" />, group: 'TUCKY', tappable: true },
    { field: 'tuckyPosition', featureName: 'thermometer', alertKey: 'pos_alert', colorMode: 'bgOnly',
        formatKey: 'state', labelId: 'param.position', defaultLabel: 'Position',
        renderIcon: () => imgIcon(stand_alert, 'position-stand-alert'), group: 'TUCKY', tappable: false },
    { field: 'oximeterPulseRate', featureName: 'oximeter', alertKey: 'PR_alert', colorMode: 'full',
        formatKey: 'PR_alert', labelId: 'patientConstants.heartRate', defaultLabel: 'Heart rate',
        renderIcon: () => <Icon name="heartbeat" />, group: 'Oximeter', tappable: true },
    { field: 'oximeterSPO2', featureName: 'oximeter', alertKey: 'SPO2_alert', colorMode: 'full',
        formatKey: 'SPO2_alert', labelId: 'patientConstants.spo2', defaultLabel: 'SPO2',
        renderIcon: () => imgIcon(SPO2_grey, 'SPO2_logo'), group: 'Oximeter', tappable: true },
    { field: 'tensiometerPulseRate', featureName: 'tensiometer', alertKey: 'bloodpressure_PR_alert', colorMode: 'full',
        formatKey: 'bloodpressure_PR_alert', labelId: 'patientConstants.heartRate', defaultLabel: 'Heart rate',
        renderIcon: () => <Icon name="heartbeat" />, group: 'Tensiometer', tappable: true },
    { field: 'tensiometerBloodpressure', featureName: 'tensiometer', alertKey: 'bloodpressure_sys_high_alert', colorMode: 'full',
        formatKey: 'bloodpressure_sys_high_alert', labelId: 'patientConstants.pressureBlood', defaultLabel: 'Bloodpressure',
        renderIcon: () => imgIcon(bloodPressure_grey, 'Bloodpressure_logo'), group: 'Tensiometer', tappable: true },
    { field: 'scaleWeight', featureName: 'scale', alertKey: 'weight_alert', colorMode: 'full',
        formatKey: 'weight_alert', labelId: 'patientConstants.weight', defaultLabel: 'Weight',
        renderIcon: () => imgIcon(weight_grey, 'Weight_logo'), group: 'Scale', tappable: true },
    { field: 'NIVUsage', featureName: 'niv', alertKey: 'breathing_observance_alert', colorMode: 'full',
        formatKey: 'breathing_observance_alert', labelId: 'breathing.niv.usage', defaultLabel: 'NIV usage',
        renderIcon: () => <Icon name="clock" />, group: 'NIV', tappable: true },
    { field: 'NIVLeak', featureName: 'niv', alertKey: 'breathing_leakage_alert', colorMode: 'full',
        formatKey: 'breathing_leakage_alert', labelId: 'param.breathingLeakageAlert', defaultLabel: 'Leakage',
        renderIcon: () => imgIcon(img_leakage, 'leakage_logo'), group: 'NIV', tappable: true },
    { field: 'NIVRate', featureName: 'niv', alertKey: 'breathing_rate_alert', colorMode: 'full',
        formatKey: 'breathing_rate_alert', labelId: 'param.breathingAlert', defaultLabel: 'Breathing rate',
        renderIcon: () => imgIcon(img_vni, 'niv_logo'), group: 'NIV', tappable: true },
    { field: 'OxiUsage', featureName: 'oxigen_therapy', alertKey: null, colorMode: 'none',
        formatKey: 'breathing_observance_alert', labelId: 'breathing.oxygen_therapy.usage', defaultLabel: 'Oxygen therapy usage',
        renderIcon: () => <Icon name="clock" />, group: 'Oxigen therapy', tappable: true },
    // formatKey typo ("oxigen") kept on purpose: the grid uses the same key, both views must format alike
    { field: 'OxiRate', featureName: 'oxigen_therapy', alertKey: 'breathing_oxygen_rate_alert', colorMode: 'full',
        formatKey: 'breathing_oxigen_rate_alert', labelId: 'patientConstants.oxigenFlowRate', defaultLabel: 'Oxigen flow',
        renderIcon: () => imgIcon(img_oxygentherapy, 'oxygentherapy_logo'), group: 'Oxigen therapy', tappable: true },
    { field: 'pain', featureName: 'pain', alertKey: 'pain_alert', colorMode: 'full',
        formatKey: null, labelId: 'patientConstants.pain', defaultLabel: 'Pain',
        renderIcon: () => <GiBackPain size={18} />, group: 'Healthforms', tappable: true },
    { field: 'EQ5D', featureName: 'healthform_EQ5D', alertKey: 'eq5d_alert', colorMode: 'full',
        formatKey: null, labelId: null, defaultLabel: 'EQ5D',
        renderIcon: () => imgIcon(healthscale_grey, 'healthsform_logo'), group: 'Healthforms', tappable: true },
    { field: 'EQ5DHealthscale', featureName: 'healthform_EQ5D', alertKey: 'eq5d_scale_alert', colorMode: 'full',
        formatKey: null, labelId: 'eq5d.healthscale', defaultLabel: 'EQ5D healthscale',
        renderIcon: () => imgIcon(ruler_grey, 'healthscale_logo'), group: 'Healthforms', tappable: true },
    { field: 'SF36', featureName: 'healthform_SF36', alertKey: 'sf36_alert', colorMode: 'fixedGreen',
        formatKey: 'SF36', labelId: null, defaultLabel: 'SF36',
        renderIcon: () => <TbFileSmile size={18} />, group: 'Healthforms', tappable: true },
    { field: 'MDASI', featureName: 'healthform_MDASI', alertKey: 'mdasi_alert', colorMode: 'full',
        formatKey: 'MDASI', labelId: null, defaultLabel: 'MDASI',
        renderIcon: () => <LiaSmileWinkSolid size={18} />, group: 'Healthforms', tappable: true },
    { field: 'Alimentation', featureName: 'healthform_ALIMENTATION', alertKey: null, colorMode: 'fixedGreen',
        formatKey: 'Alimentation', labelId: 'alimentation.title', defaultLabel: 'Nourishment',
        renderIcon: () => <Icon name="utensils" />, group: 'Healthforms', tappable: true },
];

export default patientMetricDescriptors;
